/**
 * Inicialização comum às duas entradas do SPA: a aplicação da equipe
 * (`main.tsx`) e o portal do associado (`portal-main.tsx`, PORTAL-01). As duas
 * páginas têm a mesma CSP com Trusted Types, então precisam da mesma policy
 * 'default' antes do primeiro render.
 */

type DevTrustedTypesPolicyFactory = {
  defaultPolicy?: unknown
  createPolicy: (
    name: 'default',
    rules: {
      createScriptURL: (value: string) => string
      createHTML: (value: string) => string
    },
  ) => unknown
}

export async function enableMockBff() {
  // MSW fica DESLIGADO por padrão: `npm run dev` consome a API real (Go :8080)
  // via proxy do Vite. O mock BFF só sobe quando explicitamente ligado
  // (VITE_ENABLE_MSW=true) — uso restrito a testes/Storybook/exploração offline.
  if (import.meta.env.VITE_ENABLE_MSW !== 'true') {
    return
  }
  const { worker } = await import('@/mocks/browser')
  await worker.start({
    serviceWorker: {
      url: '/mockServiceWorker.js',
    },
    onUnhandledRequest: 'bypass',
  })
}

// setupTrustedTypes registra a policy 'default' (nome já liberado pelo
// `trusted-types` do CSP em index.html) em TODO ambiente, dev e produção.
//
// Achado ao vivo (10/08, demo local em build de produção real): sem policy
// 'default' nenhuma em prod, QUALQUER innerHTML= cru — inclusive de
// bibliotecas de terceiro sem noção de Trusted Types (Radix `SelectViewport`
// injeta seu próprio <style> assim, ReactQueryDevtools idem) — lança
// TypeError síncrono durante o render. Como nenhum boundary de erro é
// modular hoje (#356), isso derruba a rota inteira — e como o Select
// (#292) é usado em 58 pontos/25 arquivos, na prática qualquer tela com
// filtro quebrava em produção.
//
// createHTML é pass-through seguro de propósito: `grep dangerouslySetInnerHTML=`
// no app inteiro dá ZERO ocorrências reais (chart.tsx evita isso deliberadamente,
// ver #256) — não existe dado de usuário passando por aqui pra sanitizar. A
// "blindagem" de deixar a policy ausente em prod não bloqueava nenhum vetor
// real; só quebrava biblioteca legítima. createScriptURL continua restrito:
// libera só o service worker das notificações push (`/sw.js`, mesma origem,
// todo ambiente) e o worker do MSW (só em dev).
export function setupTrustedTypes() {
  const trustedTypes = (
    globalThis as typeof globalThis & {
      trustedTypes?: DevTrustedTypesPolicyFactory
    }
  ).trustedTypes
  if (!trustedTypes || trustedTypes.defaultPolicy) return

  const isDev = import.meta.env.DEV
  trustedTypes.createPolicy('default', {
    createScriptURL(value) {
      const url = new URL(value, window.location.origin)
      const mesmaOrigem = url.origin === window.location.origin && url.search === '' && url.hash === ''
      if (mesmaOrigem && url.pathname === '/sw.js') {
        return url.href
      }
      if (isDev && url.origin === window.location.origin && url.pathname === '/mockServiceWorker.js') {
        return url.href
      }
      throw new TypeError('TrustedScriptURL bloqueado pela política de desenvolvimento')
    },
    createHTML: (html) => html,
  })
}

